import TemplatizedTextField, { InputFocusRef } from '../../templatized-text-field/templatized-text-field'
import { getClassNames } from '../../../_utils/classnames'
import ColorPickerPopover from '../../color-picker-popover/color-picker-popover'
import { Button, Input, Select, Switch, Tooltip } from 'antd'
import colorConvert from 'color-convert'
import * as React from 'react'
import { useRef } from 'react'
import {
    AppMessageTemplateButtonConfig,
    MutableAppMessageTemplateButtonConfig,
} from '@pushly/models/lib/structs/app-messages/app-message-template-button-config'
import { CtaButtonType, CtaGroupTuple } from '../types'
import { titleCase } from '../../../_utils/utils'
import { AppMessageActionType } from '@pushly/models/lib/enums/app-messages/app-message-action-type'
import { BgColorsOutlined, FontColorsOutlined } from '@ant-design/icons'
import { MacroType } from '../../macro-manager/types'
import { MacroManager } from '../../macro-manager/macro-manager'
import {
    DEFAULT_CTA_BG_COLOR,
    DEFAULT_CTA_FG_COLOR,
    DEFAULT_TOGGLEABLE_BG_COLOR,
    DEFAULT_TOGGLEABLE_FG_COLOR,
} from '../constants'

type AppMessageCtaBuilderProps = {
    value: CtaGroupTuple
    type: CtaButtonType
    rootCtas: CtaGroupTuple[]
    onChange: (change: Partial<AppMessageTemplateButtonConfig>, action: CtaButtonType) => void
    disabled: boolean
}

export const AppMessageCtaBuilder = (props: AppMessageCtaBuilderProps) => {
    const { disabled, value, rootCtas } = props

    const labelRef = useRef<InputFocusRef>(null)

    const handleChange = (change: Partial<AppMessageTemplateButtonConfig>) => {
        // cannot disable Primary when Secondary contains a value
        if ('enabled' in change && props.type === CtaButtonType.PRIMARY && !change.enabled && rootCtas[1][1].enabled) {
            return
        }

        props.onChange(change, props.type)
    }

    const localValue = value[1]
    const opposingAction = rootCtas[props.type === CtaButtonType.PRIMARY ? 0 : 1][1]
    const defaultValue = localValue.enabled
        ? localValue
        : ({
              enabled: true,
              fgColor: DEFAULT_CTA_FG_COLOR,
              bgColor: DEFAULT_CTA_BG_COLOR,
              action: opposingAction.enabled
                  ? opposingAction.action === AppMessageActionType.CLOSE
                      ? AppMessageActionType.OPEN_URL
                      : AppMessageActionType.CLOSE
                  : AppMessageActionType.OPEN_URL,
          } as MutableAppMessageTemplateButtonConfig)

    const [siblingCtaTuple] = rootCtas
        .filter(([type, _]) => {
            return type !== props.type
        })
        .map(([_, cta]) => cta)

    const utmMacroTypes: MacroType[] = ['domain', 'device', 'location', 'app_message', 'custom']

    return (
        <div className={getClassNames('app-message-cta-builder', {})}>
            <div className="switch-row">
                <Tooltip
                    title={
                        props.type === CtaButtonType.PRIMARY && rootCtas[1][1].enabled
                            ? 'Primary Button cannot be disabled while a Secondary button is enabled.'
                            : ''
                    }
                >
                    <label>
                        <Switch
                            size="small"
                            onChange={(enabled) => {
                                if (enabled) {
                                    handleChange({ ...defaultValue, enabled })
                                    if (labelRef.current) {
                                        labelRef?.current.focusInput()
                                    }
                                } else {
                                    // reset button
                                    handleChange({
                                        enabled,
                                    })
                                }
                            }}
                            checked={localValue.enabled}
                            disabled={(props.type === CtaButtonType.SECONDARY && !rootCtas[0][1].enabled) || disabled}
                        />
                        <span className="label-with-component-l">{titleCase(props.type)} Button</span>
                    </label>
                </Tooltip>
            </div>

            <div className="app-message-cta-label">
                <TemplatizedTextField
                    ref={labelRef}
                    className={getClassNames(null, 'app-message-cta-fields', 'color-field')}
                    value={localValue.label}
                    disabled={!localValue.enabled || disabled}
                    onChange={(label) => {
                        handleChange({ label })
                    }}
                    placeholder={`${titleCase(props.type)} Call-to-Action`}
                />
                <ColorPickerPopover
                    className="color-popover-btn"
                    content={
                        <Tooltip title="Font Color" trigger="hover">
                            <Button
                                disabled={!localValue.enabled || disabled}
                                style={{
                                    color:
                                        colorConvert.hex.hsl(localValue.fgColor ?? defaultValue.fgColor!)?.[2] < 70
                                            ? 'white'
                                            : 'black' ?? 'black',
                                }}
                            >
                                <span
                                    className="color-wrap"
                                    style={{
                                        backgroundColor: localValue.fgColor ?? defaultValue.fgColor,
                                    }}
                                >
                                    <FontColorsOutlined />
                                </span>
                            </Button>
                        </Tooltip>
                    }
                    disabled={!localValue.enabled || disabled}
                    defaultColor={defaultValue.fgColor!}
                    color={localValue.fgColor ?? defaultValue.fgColor!}
                    onChange={(color) => {
                        handleChange({ fgColor: color!.toString() })
                    }}
                />
                <ColorPickerPopover
                    className="color-popover-btn"
                    content={
                        <Tooltip title="Background Color" trigger="hover">
                            <Button
                                disabled={!localValue.enabled || disabled}
                                style={{
                                    color:
                                        colorConvert.hex.hsl(localValue.bgColor ?? defaultValue.bgColor!)?.[2] < 70
                                            ? 'white'
                                            : 'black' ?? 'black',
                                }}
                            >
                                <span
                                    className="color-wrap"
                                    style={{
                                        backgroundColor: localValue.bgColor ?? defaultValue.bgColor,
                                    }}
                                >
                                    <BgColorsOutlined />
                                </span>
                            </Button>
                        </Tooltip>
                    }
                    disabled={!localValue.enabled || disabled}
                    defaultColor={defaultValue.bgColor!}
                    color={localValue.bgColor ?? defaultValue.bgColor!}
                    onChange={(color) => {
                        handleChange({ bgColor: color!.toString() })
                    }}
                />
            </div>

            <div className="app-message-action">
                <div className="action-type-select">
                    <Input.Group compact={true} size="default">
                        <span
                            className={getClassNames(null, 'ant-input-group-addon', {
                                disabled: !localValue.enabled || disabled,
                            })}
                        >
                            Action
                        </span>
                        <Select<AppMessageActionType>
                            disabled={!localValue.enabled || disabled}
                            value={localValue.action ?? AppMessageActionType.OPEN_URL}
                            defaultValue={AppMessageActionType.OPEN_URL}
                            onChange={(action) => handleChange({ action })}
                        >
                            <Select.Option value={AppMessageActionType.OPEN_URL}>Open URL</Select.Option>
                            <Select.Option
                                value={AppMessageActionType.CLOSE}
                                disabled={siblingCtaTuple.action === AppMessageActionType.CLOSE}
                            >
                                Dismiss
                            </Select.Option>
                            <Select.Option
                                value={AppMessageActionType.PROMPT_NOTIFICATIONS}
                                disabled={siblingCtaTuple.action === AppMessageActionType.PROMPT_NOTIFICATIONS}
                            >
                                Prompt For Notification Permissions
                            </Select.Option>
                        </Select>
                    </Input.Group>
                </div>

                {localValue.action === AppMessageActionType.OPEN_URL && (
                    <div className="action-type-url">
                        <MacroManager disabled={!localValue.enabled}>
                            <Input
                                className={getClassNames(null, 'url-input-group', {
                                    disabled: !localValue.enabled || disabled,
                                })}
                                placeholder="app://..."
                                addonBefore="URL"
                                disabled={!localValue.enabled || disabled}
                                value={localValue.url}
                                onChange={(ev) => handleChange({ url: ev.target.value })}
                            />
                        </MacroManager>
                    </div>
                )}
            </div>
        </div>
    )
}
